import { Box, Container, Heading, Text, SimpleGrid, Flex, Stack } from '@chakra-ui/react';
import { useColorModeValue } from '@chakra-ui/color-mode';
import { Repeat, Sun, Edit } from 'lucide-react';

interface UseCaseProps {
  title: string;
  description: string;
  icon: React.ReactElement;
}

const UseCase = ({ title, description, icon }: UseCaseProps) => {
  const bgColor = useColorModeValue('white', 'gray.700');
  
  return (
    <Box
      bg={bgColor}
      p={6}
      borderRadius="lg"
      boxShadow="md"
      transition="transform 0.3s, box-shadow 0.3s"
      _hover={{
        transform: 'translateY(-5px)',
        boxShadow: 'lg',
      }}
    >
      <Flex align="center" mb={4}>
        <Box
          bg="brand.primary"
          p={2}
          borderRadius="md"
          color="gray.800"
          mr={4}
        >
          {icon}
        </Box>
        <Heading as="h3" textStyle="subheading">
          {title}
        </Heading>
      </Flex>
      <Text color="gray.600">{description}</Text>
    </Box>
  );
};

const UseCasesSection = () => {
  return (
    <Box id="use-cases" py={20} bg="white">
      <Container maxW="1200px">
        <Box textAlign="center" mb={16}>
          <Heading
            as="h2"
            textStyle="heading"
            position="relative"
            mb={5}
            _after={{
              content: '""',
              width: '70px',
              height: '4px',
              backgroundColor: 'brand.secondary',
              position: 'absolute',
              bottom: '-10px',
              left: '50%',
              transform: 'translateX(-50%)',
            }}
          >
            Real Problems, Real Solutions
          </Heading>
          <Text textStyle="body" color="gray.600" maxW="800px" mx="auto" mt={8}>
            See how DIYBuddy can help with common household issues and projects
          </Text>
        </Box>

        <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={8}>
          <UseCase
            icon={<Repeat size={24} />}
            title="Dishwasher Making Strange Noises"
            description="DIYBuddy helps you identify the source of the noise, diagnose the issue, and provides step-by-step instructions to fix it or recommends a professional if needed."
          />

          <UseCase
            icon={<Sun size={24} />}
            title="Broken Sprinkler System Part"
            description="Found a leaking part in your yards sprinkler system? DIYBuddy helps you identify the part, and where to purchase a replacement so you can fix it yourself."
          />
          
          <UseCase
            icon={<Edit size={24} />}
            title="Painting Interior Walls"
            description="Get detailed step-by-step guidance for painting interior walls, from surface preparation to final coat. Includes a list of suggested materials and application techniques."
          />
        </SimpleGrid>
      </Container>
    </Box>
  );
};

export default UseCasesSection;
